/**
 * Application entry point.
 * Sets up React, Ant Design theme, and React Query provider.
 * 
 * This file:
 * - Mounts the React app to #root
 * - Configures Ant Design with dark theme
 * - Sets up TanStack Query for server state management
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider, theme } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';

// Import highlight.js styles for code syntax highlighting in essays
import 'highlight.js/styles/github-dark.css';

// =============================================================================
// React Query Setup
// =============================================================================

/**
 * TanStack Query client for managing server state.
 * Handles caching, refetching, and synchronization of API data.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes before considering it stale
      staleTime: 5 * 60 * 1000,
      // Retry failed requests up to 3 times
      retry: 3,
      // Don't refetch on window focus in development
      refetchOnWindowFocus: import.meta.env.PROD,
    },
  },
});

// =============================================================================
// Ant Design Theme Configuration
// =============================================================================

/**
 * Dark theme configuration for Ant Design.
 * Uses dark grey backgrounds with white text.
 * Inspired by Ghost publishing platform aesthetic.
 */
const darkTheme = {
  // Use Ant Design's built-in dark algorithm
  algorithm: theme.darkAlgorithm,
  token: {
    // Primary brand color - a deep teal for distinction
    colorPrimary: '#d4af37',
    // Background colors - dark greys
    colorBgBase: '#121212',
    colorBgContainer: '#1a1a1a',
    colorBgElevated: '#242424',
    // Text colors - white/light grey
    colorText: '#f5f5f5',
    colorTextSecondary: '#a0a0a0',
    // Border colors
    colorBorder: '#333333',
    colorBorderSecondary: '#222222', // --border-subtle
    // Link color
    colorLink: '#e0bf4a',
    colorLinkHover: '#ecd06d',
    // Font settings
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    // Border radius
    borderRadius: 6,
  },
  components: {
    // Layout component customization
    Layout: {
      headerBg: '#141414',
      bodyBg: '#121212',
      siderBg: '#141414',
    },
    // Card component customization
    Card: {
      colorBgContainer: '#1a1a1a',
    },
    // Menu component customization
    Menu: {
      darkItemBg: 'transparent',
      darkItemSelectedBg: '#2a2a2a',
    },
  },
};

// =============================================================================
// Render Application
// =============================================================================

/**
 * Mount the React application to the DOM.
 */
const root = document.getElementById('root');
if (!root) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={darkTheme}>
        <App />
      </ConfigProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
